import {
  ClassifiedError,
  DEFAULT_SETTINGS,
  kindFromStatus,
  WS_PROTOCOL,
  type ActScope,
  type ComposeAttachment,
  type ListOptions,
  type ListResult,
  type MessageAction,
  type ConnectionInfo,
  type ConnectionState,
  type MailEvent,
  type MomomailBridge,
  type ServerEvent,
  type Settings,
  type ShellBridge,
  type ShellPrefs,
  type ShellReplica,
  type WsFrame,
} from '@momomail/shared';
import { t, tMessage } from './i18n';
import { friendlyError, friendlyText } from './friendly-error';

/**
 * The UI's single entry point to the backend: HTTP JSON API + WebSocket push, implementing MomomailBridge (UI components barely need to change).
 * - On desktop: window.momomailShell (preload) provides the backend URL and token, plus shell capabilities like window, attachment and tray events;
 * - In a regular browser: the backend is the current URL, and the session token after login is stored in localStorage.
 * The WebSocket reconnects automatically when dropped (resuming with boot/seq); if it can't resume, emit a changed event so the UI refetches everything.
 *
 * Desktop remote mode has a local replica (shell.replica, in the main process): lists, folders, unread counts and opened messages are read from the replica;
 * actions update the replica first (optimistic update), then go to the backend; on failure the replica is reverted to match the backend. The backend's changed events are received by the main process, which pulls the delta and
 * then notifies the page with a replica event (otherwise the page would read before the replica is updated). When the backend is unreachable, the replica still works if it has data.
 */

declare global {
  interface Window {
    momomailShell?: ShellBridge;
  }
}

export const shell: ShellBridge | undefined = window.momomailShell;

/**
 * This run of the window, for the server to tell its requests apart from another window's or device's: 馍馍 works its screen under
 * it (lib/screen.ts), and the event WebSocket goes under it (?client=), so the server notices when the window is closed, killed or
 * offline and stops waiting for its screen.
 */
export const screenClient = newClient();

function newClient(): string {
  // (randomUUID is only there on https and localhost: the web version may be served over plain http on the home network)
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
}

const TOKEN_KEY = 'momomail.token';

export class AuthRequiredError extends Error {}
/** Network unreachable (not a backend error). */
export class NetworkError extends Error {}

const SETTINGS_KEY = 'momomail.settings';

class Client {
  info: ConnectionInfo = { mode: shell ? 'local' : 'browser', url: '', token: null, error: null };
  private state: ConnectionState = 'connecting';
  private stateListeners = new Set<(s: ConnectionState) => void>();
  private listeners = new Set<(e: MailEvent) => void>();
  private ws: WebSocket | null = null;
  private boot: string | null = null;
  private seq = 0;
  private attempt = 0;
  private everOnline = false;
  private retryTimer: number | null = null;
  private watchdog: number | null = null;
  prefs: ShellPrefs = { launchAtLogin: false };
  /** The remote-mode local replica is ready (has completed a full sync once). */
  replicaReady = false;
  /** The most recent settings (the conversation-view toggle decides how to query the replica; also used when offline). */
  settings: Settings = { ...DEFAULT_SETTINGS, ...(json<Partial<Settings>>(load(SETTINGS_KEY)) ?? {}) };

  private initPromise: Promise<void> | null = null;

  /** Run only once (effects run twice under React StrictMode). */
  init(): Promise<void> {
    return (this.initPromise ??= this.doInit());
  }

  private async doInit(): Promise<void> {
    if (shell) {
      this.info = await shell.connection();
      this.prefs = await shell.prefs.get().catch(() => this.prefs);
      if (this.info.mode === 'remote' && shell.replica) this.replicaReady = !!(await shell.replica.status().catch(() => null))?.ready;
      shell.onEvent((e) => this.dispatch(e));
    } else {
      // For development: #token=… brings the token in directly (pnpm server --dev prints a URL like this)
      const m = /[#&]token=([^&]+)/.exec(location.hash);
      if (m) {
        save(TOKEN_KEY, decodeURIComponent(m[1]!));
        history.replaceState(null, '', location.pathname + location.search);
      }
      this.info = { mode: 'browser', url: location.origin, token: load(TOKEN_KEY), error: null };
    }
    if (!this.info.url || !this.info.token) {
      this.setState(this.info.error ? 'connecting' : 'auth-required');
      return;
    }
    void this.connect();
  }

  getState() {
    return this.state;
  }

  private setState(s: ConnectionState) {
    if (s === this.state) return;
    this.state = s;
    for (const l of this.stateListeners) l(s);
  }

  onState(cb: (s: ConnectionState) => void) {
    this.stateListeners.add(cb);
    return () => void this.stateListeners.delete(cb);
  }

  onEvent(cb: (e: MailEvent) => void) {
    this.listeners.add(cb);
    return () => void this.listeners.delete(cb);
  }

  dispatch(e: MailEvent) {
    if (e.type === 'prefs') {
      // The desktop's own settings changed there: the page's settings carry them
      this.prefs = { ...this.prefs, ...e.prefs };
      e = { type: 'settings', settings: this.settings };
    }
    if (e.type === 'settings') {
      this.rememberSettings(e.settings);
      e = { ...e, settings: { ...e.settings, launchAtLogin: this.prefs.launchAtLogin, desktopMascot: this.prefs.desktopMascot ?? true } };
    }
    if (e.type === 'replica') {
      // The replica pulled a delta: the UI refreshes as if it got changed
      this.replicaReady = e.ready;
      e = { type: 'changed', accountIds: e.accountIds };
    } else if (e.type === 'changed' && this.replica()) return; // Wait for the main process to pull the delta into the replica; it then notifies via a replica event
    for (const l of this.listeners) l(e);
  }

  rememberSettings(s: Settings) {
    this.settings = s;
    save(SETTINGS_KEY, JSON.stringify(s));
  }

  /** The local replica currently usable (desktop remote mode, synced at least once). */
  replica(): ShellReplica | null {
    return this.info.mode === 'remote' && this.replicaReady && shell?.replica ? shell.replica : null;
  }

  private authLost() {
    this.ws?.close();
    this.ws = null;
    if (shell) void shell.forgetToken().catch(() => {});
    else remove(TOKEN_KEY);
    this.info = { ...this.info, token: null };
    this.setState('auth-required');
  }

  async request<T>(method: string, path: string, body?: unknown, raw?: { body: BodyInit; headers: Record<string, string> }): Promise<T> {
    if (!this.info.token) throw new AuthRequiredError(t('需要登录'));
    let res: Response;
    try {
      res = await fetch(`${this.info.url}${path}`, {
        method,
        headers: { Authorization: `Bearer ${this.info.token}`, ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...raw?.headers },
        ...(body !== undefined ? { body: JSON.stringify(body) } : raw ? { body: raw.body } : {}),
      });
    } catch {
      throw new NetworkError(t('连不上邮件服务，请检查网络'));
    }
    if (res.status === 401) {
      this.authLost();
      throw new AuthRequiredError(t('登录已失效，请重新登录'));
    }
    const data = (await res.json().catch(() => null)) as unknown;
    if (!res.ok) throw new Error((data as { error?: string } | null)?.error ?? t('请求失败（{status}）', { status: res.status }));
    return data as T;
  }

  async download(path: string): Promise<Blob> {
    const res = await fetch(`${this.info.url}${path}`, { headers: { Authorization: `Bearer ${this.info.token}` } }).catch(() => null);
    if (!res) throw new Error(t('连不上邮件服务'));
    if (res.status === 401) {
      this.authLost();
      throw new AuthRequiredError(t('登录已失效，请重新登录'));
    }
    if (!res.ok) throw new Error(((await res.json().catch(() => null)) as { error?: string } | null)?.error ?? t('下载失败（{status}）', { status: res.status }));
    return res.blob();
  }

  // ---------------- WebSocket ----------------

  private async connect() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (!this.info.token) return;
    // The browser's WebSocket can't see a 401, so first confirm the token is still valid with a regular request
    try {
      const res = await fetch(`${this.info.url}/api/session`, { headers: { Authorization: `Bearer ${this.info.token}` } });
      if (res.status === 401) return this.authLost();
    } catch {
      return this.retry();
    }
    const q = `?client=${encodeURIComponent(screenClient)}${this.boot ? `&boot=${encodeURIComponent(this.boot)}&since=${this.seq}` : ''}`;
    const ws = new WebSocket(`${this.info.url.replace(/^http/, 'ws')}/api/ws${q}`, [WS_PROTOCOL, `bearer.${this.info.token}`]);
    this.ws = ws;
    ws.onmessage = (msg) => {
      this.kick();
      let f: WsFrame;
      try {
        f = JSON.parse(String(msg.data)) as WsFrame;
      } catch {
        return;
      }
      if (f.kind === 'hello') {
        const resync = this.everOnline && !f.resumed;
        this.boot = f.boot;
        this.seq = f.seq;
        this.attempt = 0;
        this.everOnline = true;
        this.setState('online');
        this.rememberSettings(f.settings);
        if (resync) {
          // Events during the disconnect can't be recovered: fill in from the state carried by hello, then have the UI refetch everything
          this.dispatch({ type: 'settings', settings: f.settings });
          for (const status of f.statuses) this.dispatch({ type: 'status', status });
          this.dispatch({ type: 'changed', accountIds: [] });
        }
      } else if (f.kind === 'event') {
        this.seq = f.seq;
        this.dispatch(f.event as ServerEvent);
      }
    };
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (e.code === 4401) return this.authLost();
      this.retry();
    };
  }

  private kick() {
    if (this.watchdog) clearTimeout(this.watchdog);
    this.watchdog = window.setTimeout(() => this.ws?.close(), 70_000);
  }

  private retry() {
    if (this.state === 'auth-required') return;
    // With a local replica, go into the main UI even when unreachable (showing "正在重新连接" (Reconnecting)), still usable for reading
    this.setState(this.everOnline || this.replica() ? 'reconnecting' : 'connecting');
    const delay = Math.min(10_000, 400 * 2 ** Math.min(this.attempt++, 5));
    this.retryTimer = window.setTimeout(() => void this.connect(), delay);
  }

  /** Retry immediately (the "重新连接" (Reconnect) button in the UI). */
  reconnectNow() {
    this.attempt = 0;
    void this.connect();
  }

  // ---------------- Login ----------------

  async login(url: string, username: string, password: string) {
    if (shell) {
      await shell.connectRemote(url, username, password); // The main process logs in and saves, then the page reloads
      return;
    }
    let res: Response;
    try {
      res = await fetch(`${location.origin}/api/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password }) });
    } catch (error) {
      throw new ClassifiedError(navigator.onLine === false ? 'offline' : 'unreachable', String((error as Error)?.message ?? error));
    }
    const data = (await res.json().catch(() => ({}))) as { token?: string; error?: string };
    if (!res.ok || !data.token) throw new ClassifiedError(kindFromStatus(res.status), data.error ?? `HTTP ${res.status}`);
    await this.useToken(data.token);
  }

  /** Web version: use a freshly obtained session token (login, or auto-login after accepting an invite). */
  async useToken(token: string) {
    save(TOKEN_KEY, token);
    this.info = { ...this.info, token };
    this.everOnline = false;
    this.boot = null;
    this.setState('connecting');
    await this.connect();
  }

  async logout() {
    await this.request('POST', '/api/logout').catch(() => {});
    this.authLost();
  }
}

function json<T>(text: string | null): T | null {
  if (!text) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

export function load(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
export function save(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}
function remove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export const client = new Client();

const enc = encodeURIComponent;

/** Types the browser can view directly in a new tab; everything else is downloaded (opening HTML, SVG and the like under this site's origin is risky). */
const VIEWABLE = /^(image\/(png|jpeg|gif|webp)|application\/pdf|text\/plain)$/;

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function chooseFiles(): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.style.display = 'none';
    input.addEventListener('change', () => {
      resolve(Array.from(input.files ?? []));
      input.remove();
    });
    input.addEventListener('cancel', () => {
      resolve([]);
      input.remove();
    });
    document.body.appendChild(input);
    input.click();
  });
}

async function upload(files: File[]): Promise<ComposeAttachment[]> {
  const out: ComposeAttachment[] = [];
  for (const f of files) {
    out.push(await client.request<ComposeAttachment>('POST', '/api/uploads', undefined, { body: f, headers: { 'Content-Type': f.type || 'application/octet-stream', 'X-Filename': enc(f.name) } }));
  }
  return out;
}

const withPrefs = (s: Settings): Settings => ({ ...DEFAULT_SETTINGS, ...s, launchAtLogin: client.prefs.launchAtLogin, desktopMascot: client.prefs.desktopMascot ?? true });

/** Ask the backend first; when the network is down and the local replica is usable, fall back to the replica (readable offline). */
async function orOffline<T>(remote: () => Promise<T>, offline: (() => Promise<T>) | null): Promise<T> {
  try {
    return await remote();
  } catch (error) {
    if (error instanceof NetworkError && offline) return offline();
    throw error;
  }
}

/**
 * Lists: serve what the replica has directly; past the replica's earliest day (or when the replica has less than a page), ask the backend for the rest (older mail, backend loads further back).
 * Search always asks the backend (it has to search the mail server).
 */
async function listMessages(options: ListOptions): Promise<ListResult> {
  const rep = client.replica();
  if (!rep || options.ref.kind === 'search') return client.request('POST', '/api/messages/query', options);
  const conversation = client.settings.conversationView;
  const local = await rep.list(options, conversation);
  if (local.hasMore) return local;
  const last = local.messages.at(-1);
  const before = last ? { date: last.date, id: last.id } : options.before;
  const rest = options.limit - local.messages.length;
  try {
    const more = await client.request<ListResult>('POST', '/api/messages/query', { ...options, before, limit: Math.max(1, rest) });
    const seen = new Set(local.messages.map((m) => m.id));
    const extra = more.messages.filter((m) => !seen.has(m.id)).slice(0, Math.max(0, rest));
    return { messages: [...local.messages, ...extra], hasMore: more.hasMore || more.messages.length > extra.length };
  } catch (error) {
    if (error instanceof NetworkError) return local;
    throw error;
  }
}

/** Actions: update the replica first (UI sees it immediately), then have the backend do it; if the backend fails, revert the replica to match the backend. */
async function actOn(ids: number[], action: MessageAction, scope: ActScope | null): Promise<void> {
  const rep = client.replica();
  const touched = rep ? await rep.apply(ids, action, scope).catch(() => [] as number[]) : [];
  try {
    await client.request('POST', '/api/messages/actions', { ids, action, scope });
  } catch (error) {
    if (rep && touched.length) void rep.revert(touched).catch(() => {});
    throw error;
  }
}

const prefetched = new Set<string>();

export const bridge: MomomailBridge = {
  apiVersion: 2,
  platform: shell?.platform ?? 'web',
  desktop: !!shell,
  app: {
    version: shell?.version ?? '',
    quit: () => shell?.quit(),
  },
  window: shell?.window ?? null,
  connection: {
    info: () => client.info,
    state: () => client.getState(),
    onState: (cb) => client.onState(cb),
    login: (url, username, password) => client.login(url, username, password),
    logout: () => client.logout(),
    useLocal: async () => {
      await shell?.useLocal();
    },
  },
  accounts: {
    list: () => {
      const rep = client.replica();
      return orOffline(() => client.request('GET', '/api/accounts'), rep ? () => rep.accounts() : null);
    },
    presets: () => orOffline(() => client.request('GET', '/api/presets'), client.replica() ? async () => [] : null),
    test: (input) => client.request('POST', '/api/accounts/test', input),
    save: (input) => client.request('POST', '/api/accounts', input),
    remove: async (id) => void (await client.request('DELETE', `/api/accounts/${enc(id)}`)),
    reorder: async (ids) => void (await client.request('PUT', '/api/accounts/order', { ids })),
    status: () => orOffline(() => client.request('GET', '/api/accounts/status'), client.replica() ? async () => [] : null),
  },
  mail: {
    // Folder unread / total counts and unified-view counts must include old mail outside the replica window: ask the backend when online (small data), use the replica when offline
    folders: (accountId) => {
      const rep = client.replica();
      return orOffline(() => client.request('GET', `/api/folders${accountId ? `?accountId=${enc(accountId)}` : ''}`), rep ? () => rep.folders(accountId) : null);
    },
    unifiedCounts: () => {
      const rep = client.replica();
      return orOffline(
        async () => (await client.request<{ unified: Awaited<ReturnType<MomomailBridge['mail']['unifiedCounts']>> }>('GET', '/api/counts')).unified,
        rep ? () => rep.counts() : null,
      );
    },
    // (the backend only: what the AI tagged includes mail outside the replica window)
    tagCounts: () => client.request('GET', '/api/tag-counts'),
    junkToSpam: async () => (await client.request<{ moved: number }>('POST', '/api/tags/junk/spam')).moved,
    list: listMessages,
    // Throw when offline: the compose page falls back to suggesting only addresses from the messages at hand
    contacts: (query, exclude) => client.request('GET', `/api/contacts?q=${enc(query)}&exclude=${enc(exclude.join(','))}`),
    thread: (id, conversation, remote) => {
      const rep = client.replica();
      return rep ? rep.thread(id, conversation, remote) : client.request('GET', `/api/messages/${id}/thread?conversation=${conversation ? 1 : 0}&remote=${remote ? 1 : 0}`);
    },
    act: actOn,
    addAiEvent: (messageId) => client.request('POST', `/api/messages/${messageId}/ai-event`),
    undoAiEvent: (messageId) => client.request('DELETE', `/api/messages/${messageId}/ai-event`),
    dismissAi: (messageId, kind) => client.request('POST', `/api/messages/${messageId}/ai-dismiss`, { kind }),
    unsubscribe: (id, confirmMail) => client.request('POST', `/api/messages/${id}/unsubscribe`, { confirmMail }),
    unsubscribeDone: (id) => client.request('POST', `/api/messages/${id}/unsubscribe/done`),
    markAllRead: async (ref, accountId, onServer) => {
      const rep = client.replica();
      if (rep) await rep.markAllRead(ref, accountId, onServer === true).catch(() => {});
      await client.request('POST', '/api/messages/mark-all-read', { ref, accountId, onServer: onServer === true });
    },
    sync: async (accountId) => void (await client.request('POST', '/api/sync', { accountId })),
    prefetch: (ids, conversation) => {
      const rep = client.replica();
      if (rep) {
        void rep.prefetch(ids, conversation).catch(() => {});
        return;
      }
      // No replica: have the backend fetch the bodies from the mail server first (the backend caches them), so opening is fast
      for (const id of ids.slice(0, 3)) {
        const key = `${id}:${conversation}`;
        if (prefetched.has(key)) continue;
        prefetched.add(key);
        if (prefetched.size > 500) prefetched.clear();
        void client.request('GET', `/api/messages/${id}/thread?conversation=${conversation ? 1 : 0}&remote=0`).catch(() => prefetched.delete(key));
      }
    },
    openAttachment: async (messageId, index, filename) => {
      if (shell) return shell.openAttachment(messageId, index, filename);
      const blob = await client.download(`/api/messages/${messageId}/attachments/${index}`);
      if (VIEWABLE.test(blob.type)) {
        const url = URL.createObjectURL(new Blob([blob], { type: blob.type }));
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else triggerDownload(blob, filename);
    },
    saveAttachment: async (messageId, index, filename) => {
      if (shell) return shell.saveAttachment(messageId, index, filename);
      triggerDownload(await client.download(`/api/messages/${messageId}/attachments/${index}`), filename);
      return true;
    },
    openExternal: async (url) => {
      if (shell) return shell.openExternal(url);
      if (/^mailto:/i.test(url)) {
        client.dispatch({ type: 'compose', to: decodeURIComponent(url.slice(7).split('?')[0] ?? '') });
        return;
      }
      if (/^https?:/i.test(url)) window.open(url, '_blank', 'noopener,noreferrer');
    },
  },
  compose: {
    create: (mode, sourceMessageId, accountId) => client.request('POST', '/api/compose', { mode, sourceMessageId, accountId }),
    saveLocal: async (draft) => void (await client.request('PUT', '/api/drafts/local', draft)),
    saveDraft: (draft) => client.request('POST', '/api/drafts', draft),
    discard: async (draft) => void (await client.request('POST', '/api/drafts/discard', draft)),
    send: (draft) => client.request('POST', '/api/send', draft),
    pickFiles: async () => upload(await chooseFiles()),
    upload,
    localDrafts: () => orOffline(() => client.request('GET', '/api/drafts/local'), client.replica() ? async () => [] : null),
  },
  settings: {
    get: async () => {
      const s = await orOffline(() => client.request<Settings>('GET', '/api/settings'), client.replica() ? async () => client.settings : null);
      client.rememberSettings(s);
      return withPrefs(s);
    },
    update: async (patch) => {
      // Settings local to this computer go to the desktop shell, the rest to the backend
      const { launchAtLogin, desktopMascot, ...rest } = patch;
      if (launchAtLogin !== undefined && shell) client.prefs = await shell.prefs.set({ launchAtLogin });
      if (desktopMascot !== undefined && shell) client.prefs = await shell.prefs.set({ desktopMascot });
      const s = Object.keys(rest).length ? await client.request<Settings>('PATCH', '/api/settings', rest) : await client.request<Settings>('GET', '/api/settings');
      client.rememberSettings(s);
      return withPrefs(s);
    },
  },
  assistant: {
    state: () => client.request('GET', '/api/assistant'),
    send: (text, timeZone, context) => client.request('POST', '/api/assistant/messages', { text, timeZone, context }),
    act: (actionId, approve) => client.request('POST', `/api/assistant/actions/${enc(actionId)}`, { approve }),
    stop: () => client.request('POST', '/api/assistant/stop'),
    clear: () => client.request('DELETE', '/api/assistant/messages'),
    openConversation: (id) => client.request('POST', `/api/assistant/conversations/${enc(id)}/open`),
    deleteConversation: (id) => client.request('DELETE', `/api/assistant/conversations/${enc(id)}`),
    memory: async () => ((await client.request('GET', '/api/assistant/memory')) as { memory: string }).memory,
    uiAnswer: async (id, answer) => void (await client.request('POST', `/api/assistant/ui/${enc(id)}`, answer)),
    presence: async (id, present) => void (await client.request('POST', '/api/assistant/presence', { client: id, present })),
  },
  ai: {
    overview: () => client.request('GET', '/api/ai'),
    select: (id) => client.request('PUT', '/api/ai/selected', { id }),
    useLocal: (id) => client.request('POST', '/api/ai/local', { id }),
    login: (id) => client.request('POST', '/api/ai/login', { id }),
    submitCode: (code) => client.request('POST', '/api/ai/login/code', { code }),
    cancelLogin: () => client.request('DELETE', '/api/ai/login'),
    configure: ({ id, ...rest }) => client.request('PUT', `/api/ai/connections/${enc(id)}`, rest),
    logout: (id) => client.request('DELETE', `/api/ai/connections/${enc(id)}`),
    verify: () => client.request('POST', '/api/ai/verify'),
    run: (prompt) => client.request('POST', '/api/ai/run', { prompt }),
    assign: (purpose, assignment) => client.request('PUT', `/api/ai/purposes/${purpose}`, assignment ?? { connectionId: null }),
    retag: () => client.request('POST', '/api/ai/tagging/retag'),
  },
  storage: {
    server: () => client.request('GET', '/api/storage'),
    clearServer: () => client.request('POST', '/api/storage/clear'),
    replica: async () => (client.info.mode === 'remote' && shell?.replica ? shell.replica.status() : null),
    configureReplica: async (patch) => {
      if (!shell?.replica || client.info.mode !== 'remote') return null;
      client.prefs = await shell.prefs.set({ ...(patch.days !== undefined ? { replicaDays: patch.days } : {}), ...(patch.cacheMb !== undefined ? { replicaCacheMb: patch.cacheMb } : {}) });
      return shell.replica.status();
    },
    resetReplica: async () => (client.info.mode === 'remote' && shell?.replica ? shell.replica.reset() : null),
  },
  google: {
    info: () => client.request('GET', '/api/oauth'),
    start: (purpose, options) =>
      purpose === 'calendar' ? client.request('POST', '/api/calendar/connect', { ...(options.loginHint ? { loginHint: options.loginHint } : {}) }) : client.request('POST', '/api/oauth/google/start', { purpose, ...options }),
    flow: (flowId) => client.request('GET', `/api/oauth/google/flows/${enc(flowId)}`),
    finish: (flowId, response) => client.request('POST', '/api/oauth/google/finish', { flowId, response }),
  },
  calendar: {
    info: () => client.request('GET', '/api/calendar'),
    disconnect: async (email) => void (await client.request('DELETE', `/api/calendar/links/${enc(email)}`)),
    suggest: (messageId, timeZone) => client.request('POST', `/api/messages/${messageId}/calendar/suggest`, { timeZone }),
    add: (event, messageId) => client.request('POST', '/api/calendar/events', { event, messageId }),
    respond: (messageId, status) => client.request('POST', `/api/messages/${messageId}/invite/respond`, { status }),
    connectCaldav: (input) => client.request('POST', '/api/calendar/caldav', input),
    removeAccount: async (id) => void (await client.request('DELETE', `/api/calendar/accounts/${enc(id)}`)),
    syncAccount: async (id) => void (await client.request('POST', `/api/calendar/accounts/${enc(id)}/sync`)),
    updateCalendar: (id, patch) => client.request('PATCH', `/api/calendar/calendars/${enc(id)}`, patch),
    events: (from, to) => client.request('GET', `/api/calendar/events?${new URLSearchParams({ from, to })}`),
    createEvent: (input) => client.request('POST', '/api/calendar/items', input),
    updateEvent: (id, input) => client.request('PATCH', `/api/calendar/items/${enc(id)}`, input),
    deleteEvent: async (id) => void (await client.request('DELETE', `/api/calendar/items/${enc(id)}`)),
  },
  onEvent: (cb) => client.onEvent(cb),
};

/** Save a piece of text as a file (download in the web version; on desktop it's also a browser download, and Electron shows a save dialog). */
export function downloadText(text: string, filename: string, type: string) {
  triggerDownload(new Blob([text], { type }), filename);
}

export function api(): MomomailBridge {
  return bridge;
}

/** Error message (strip the IPC prefix, just in case). */
export function errorText(error: unknown): string {
  // Known kinds (can't connect, certificate, wrong password…) get plain words; anything else is a one-line message from the backend, translated and shown as is
  const friendly = friendlyError(error);
  if (friendly.kind !== 'unknown') return friendlyText(error);
  const raw = error instanceof Error ? error.message : String(error);
  return tMessage(raw.replace(/^Error invoking remote method '[^']+': (Error: )?/, '').replace(/^Error: /, ''));
}
